import React, { useEffect, useRef } from 'react';
import { useCanvasBundle } from './canvasStore';
import { DRAG_THRESHOLD, capture, swallowNextClick } from './events';
import type { XY } from './geometry';
import { useRuntime } from './runtime';

interface Drag {
  pointerId: number;
  start: XY;
  active: boolean;
  ids: string[];
  /** Pointer minus node position, in flow units, per dragged node. */
  offsets: Record<string, XY>;
  origin: Record<string, XY>;
  /** Moved nodes whose parent is not moving with them: where each lands decides its group. */
  roots: string[];
  abortOnSecondPointer: (e: PointerEvent) => void;
}

export interface CardDragHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => void;
}

/**
 * Pointer drag for a card or a group bubble. Everything selected moves together, and
 * a group carries everything it holds; positions stay absolute, so each moved node
 * saves its own. The drop decides group membership (Canvas finishDrag).
 */
export function useCardDrag(id: string, canEdit: boolean, el: React.RefObject<HTMLElement>): CardDragHandlers {
  const rt = useRuntime();
  const bundle = useCanvasBundle();
  const drag = useRef<Drag | null>(null);

  const endDrag = (d: Drag) => {
    window.removeEventListener('pointerdown', d.abortOnSecondPointer, true);
    el.current?.classList.remove('np-dragging');
    bundle.store.getState().setDropTargets(new Set());
    drag.current = null;
  };

  const abort = (d: Drag) => {
    endDrag(d);
    if (!d.active) return;
    bundle.store.getState().moveNodes(d.origin);
    bundle.endMove(d.ids);
  };

  // Unmounted mid-drag (removed by someone else): put everything back and save nothing, or the
  // moved ids stay in the store's moving set for good and the window listener leaks.
  useEffect(
    () => () => {
      const d = drag.current;
      if (d) abort(d);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Touch never drags a node: the finger pans and pinches the pane, a tap opens the panel.
    if (!canEdit || e.pointerType === 'touch' || e.button !== 0) return;
    e.stopPropagation();
    // Trap 5, accumulated deltas drift: capture the pointer and recompute from its absolute position.
    capture(e.currentTarget, e.pointerId);
    const d: Drag = {
      pointerId: e.pointerId,
      start: { x: e.clientX, y: e.clientY },
      active: false,
      ids: [],
      offsets: {},
      origin: {},
      roots: [],
      // Trap 10, a second pointer mid-drag: put everything back rather than fling a node.
      abortOnSecondPointer: (ev: PointerEvent) => {
        if (ev.pointerId !== d.pointerId) abort(d);
      },
    };
    window.addEventListener('pointerdown', d.abortOnSecondPointer, true);
    drag.current = d;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const pointer = rt.viewport.clientToFlow(e.clientX, e.clientY);
    const s = bundle.store.getState();
    if (!d.active) {
      if (Math.hypot(e.clientX - d.start.x, e.clientY - d.start.y) < DRAG_THRESHOLD) return;
      d.active = true;
      // Grabbing an unselected node selects it alone. A group carries what it holds.
      // An authority's selection can hold nodes they may not move; those stay put.
      const base = s.selection.has(id) ? Array.from(s.selection) : [id];
      if (!s.selection.has(id)) s.selectNodes([id]);
      const all = new Set<string>();
      base.forEach((nid) => {
        all.add(nid);
        rt.descendantsOf(nid).forEach((c) => all.add(c));
      });
      Array.from(all).forEach((nid) => {
        if (nid !== id && !rt.canMove(nid)) return;
        const b = s.nodes[nid];
        if (!b) return;
        d.origin[nid] = { x: b.x, y: b.y };
        d.offsets[nid] = { x: pointer.x - b.x, y: pointer.y - b.y };
      });
      d.ids = Object.keys(d.origin);
      // The same root rule finishDrag uses, so what lights up is what the drop does.
      d.roots = d.ids.filter((nid) => {
        const parent = rt.parentOf(nid);
        return !parent || !d.origin[parent];
      });
      bundle.beginMove(d.ids);
      el.current?.classList.add('np-dragging');
    }
    const moves: Record<string, XY> = {};
    d.ids.forEach((nid) => {
      moves[nid] = { x: pointer.x - d.offsets[nid].x, y: pointer.y - d.offsets[nid].y };
    });
    s.moveNodes(moves);
    // Every bubble that would take a card lights up before the drop, so joining is never a surprise.
    const targets = new Set<string>();
    d.roots.forEach((nid) => {
      const target = rt.groupUnder(nid);
      if (target && target !== rt.parentOf(nid)) targets.add(target);
    });
    s.setDropTargets(targets);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    endDrag(d);
    if (!d.active) return;
    swallowNextClick();
    bundle.endMove(d.ids);
    rt.finishDrag(d.ids);
  };

  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (d && e.pointerId === d.pointerId) abort(d);
  };

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
